// ===================================================================
// components/SongAnalyzer.tsx
// Analyse interactive de chansons en bambara
// ===================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, ChevronRight, Play } from 'lucide-react';
import BackButton from './common/BackButton';
import {
  SONGS_BY_LANGUAGE,
  BAMBARA_GRAMMAR_STRUCTURES,
  getWordAnnotation,
  type BambaraSong,
  type GrammarStructure,
  type WordAnnotation,
  type GrammaticalCategory,
  type LyricLine,
} from '../data/songData';
import { LanguageCode } from '../types/types';
import { useTheme } from './ThemeContext';
import { useSupabaseAudio } from '../hooks/useSupabaseAudio';
import { getWordTranslation } from '../utils/localizedContent';
import './SongAnalyzer.css';

import { EmojiText } from './common/Picto';
interface SongAnalyzerProps {
  languageCode: LanguageCode;
  onBack: () => void;
}

type Screen = 'list' | 'lyrics' | 'grammar';

interface SelectedWord {
  word: string;
  annotation: WordAnnotation;
  lineIdx: number;
  wordIdx: number;
}

const CATEGORY_COLORS: Record<GrammaticalCategory, { bg: string; color: string }> = {
  pronom:       { bg: '#e3f2fd', color: '#1565c0' },
  verbe:        { bg: '#e8f5e9', color: '#2e7d32' },
  nom:          { bg: '#fff3e0', color: '#e65100' },
  postposition: { bg: '#f3e5f5', color: '#6a1b9a' },
  adjectif:     { bg: '#fce4ec', color: '#880e4f' },
  marqueur:     { bg: '#fef9c3', color: '#92400e' },
  connecteur:   { bg: '#e0f2fe', color: '#075985' },
  adverbe:      { bg: '#ecfdf5', color: '#065f46' },
  determinant:  { bg: '#fff7ed', color: '#9a3412' },
  exclamation:  { bg: '#f0fdf4', color: '#14532d' },
};

const CATEGORY_LABEL_KEYS: Record<GrammaticalCategory, string> = {
  pronom:       'songAnalyzer.categories.pronom',
  verbe:        'songAnalyzer.categories.verbe',
  nom:          'songAnalyzer.categories.nom',
  postposition: 'songAnalyzer.categories.postposition',
  adjectif:     'songAnalyzer.categories.adjectif',
  marqueur:     'songAnalyzer.categories.marqueur',
  connecteur:   'songAnalyzer.categories.connecteur',
  adverbe:      'songAnalyzer.categories.adverbe',
  determinant:  'songAnalyzer.categories.determinant',
  exclamation:  'songAnalyzer.categories.exclamation',
};

const DIFFICULTY_META = {
  debutant:      { labelKey: 'songAnalyzer.difficulty.beginner',     color: '#2e7d32', bg: '#e8f5e9' },
  intermediaire: { labelKey: 'songAnalyzer.difficulty.intermediate', color: '#e65100', bg: '#fff3e0' },
  avance:        { labelKey: 'songAnalyzer.difficulty.advanced',     color: '#6a1b9a', bg: '#f3e5f5' },
};

// Pochettes-arches du catalogue (maquette 3a)
const ARCH_COLORS = ['#C67B4E', '#A4553A', '#8A6D3B', '#B8862F', '#5E6B3A'];
function archColor(song: BambaraSong): string {
  const hash = Array.from(song.id).reduce((a, c) => a + c.charCodeAt(0), 0);
  return ARCH_COLORS[hash % ARCH_COLORS.length];
}
/** Nombre de mots expliqués (annotés) dans la chanson. */
function wordCount(song: BambaraSong): number {
  return new Set(song.lines.flatMap(l => l.words).filter(w => getWordAnnotation(song, w))).size;
}

// Catégories mises en avant dans l'analyse (petits mots qui portent la grammaire)
const KEY_CATEGORIES: GrammaticalCategory[] = ['marqueur', 'determinant', 'postposition'];

type LineAudioState = 'loading' | 'playing' | 'missing';

// Mots de la chanson déjà analysés (touchés) → progression
const progressKey = (lang: string, songId: string) => `${lang}-song-progress-${songId}`;
function readAnalyzed(lang: string, songId: string): string[] {
  try { return JSON.parse(localStorage.getItem(progressKey(lang, songId)) || '[]'); } catch { return []; }
}

function isKeyWord(song: BambaraSong, line: LyricLine, word: string): boolean {
  const ann = getWordAnnotation(song, word);
  if (!ann) return false;
  if (KEY_CATEGORIES.includes(ann.grammaticalCategory)) return true;
  const pills = (line.structure ?? '').split('+').map(s => s.trim().toUpperCase());
  return pills.includes(word.toUpperCase());
}

// Regroupe les lignes par section (REFRAIN, COUPLET 1…)
function groupLines(song: BambaraSong) {
  const groups: { title: string | null; lines: { line: LyricLine; lineIdx: number }[] }[] = [];
  song.lines.forEach((line, lineIdx) => {
    if (line.words.length === 0) {
      if (line.sectionTitle) groups.push({ title: line.sectionTitle, lines: [] });
      return;
    }
    if (groups.length === 0) groups.push({ title: null, lines: [] });
    groups[groups.length - 1].lines.push({ line, lineIdx });
  });
  return groups.filter(g => g.lines.length > 0);
}

// ─── Composant principal ──────────────────────────────────────────
const SongAnalyzer: React.FC<SongAnalyzerProps> = ({ languageCode, onBack }) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const songs = SONGS_BY_LANGUAGE[languageCode] ?? [];

  const [screen,          setScreen]          = useState<Screen>('list');
  const [currentSong,     setCurrentSong]     = useState<BambaraSong | null>(null);
  const [selectedWord,    setSelectedWord]    = useState<SelectedWord | null>(null);
  const [activeStructure, setActiveStructure] = useState<GrammarStructure | null>(null);

  const [genreFilter,     setGenreFilter]     = useState<string | null>(null);
  const [lineAudio,       setLineAudio]       = useState<Record<number, LineAudioState>>({});
  const [lastSongId,      setLastSongId]      = useState<string | null>(() => {
    try { return localStorage.getItem(`${languageCode}-song-last`); } catch { return null; }
  });
  // Incrémenté à chaque mot analysé pour recalculer les progressions
  const [, setProgressTick] = useState(0);

  const popupRef = useRef<HTMLDivElement>(null);
  const { playWord } = useSupabaseAudio(languageCode);

  // ─── Progression : part des mots annotés déjà analysés ───────
  const songProgress = useCallback((song: BambaraSong) => {
    const annotable = new Set(song.lines.flatMap(l => l.words).filter(w => getWordAnnotation(song, w)));
    if (annotable.size === 0) return 0;
    const done = readAnalyzed(languageCode, song.id).filter(w => annotable.has(w)).length;
    return Math.min(100, Math.round((done / annotable.size) * 100));
  }, [languageCode]);

  // « Reprendre » : la dernière chanson ouverte ; sinon on propose la première
  const resumeSong = songs.find(s => s.id === lastSongId) ?? null;
  const featured   = resumeSong ?? songs[0] ?? null;
  const otherSongs = songs.filter(s => s.id !== featured?.id);
  const genres     = Array.from(new Set(songs.map(s => s.genre)));
  const shelfSongs = genreFilter ? otherSongs.filter(s => s.genre === genreFilter) : otherSongs;

  // Nouvelle chanson : on oublie l'état audio des lignes
  useEffect(() => { setLineAudio({}); }, [currentSong]);

  const playLine = useCallback(async (lineIdx: number, words: string[]) => {
    setLineAudio(s => ({ ...s, [lineIdx]: 'loading' }));
    const ok = await playWord(words.join(' '));
    setLineAudio(s => ({ ...s, [lineIdx]: ok ? 'playing' : 'missing' }));
    if (ok) {
      setTimeout(() => setLineAudio(s => {
        if (s[lineIdx] !== 'playing') return s;
        const { [lineIdx]: _done, ...rest } = s;
        return rest;
      }), 2500);
    }
  }, [playWord]);

  // Ferme le popup si clic en dehors
  useEffect(() => {
    if (!selectedWord) return;
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setSelectedWord(null);
      }
    };
    // mouseup évite le conflit avec le tap initial qui a ouvert le popup
    document.addEventListener('mouseup', handler);
    return () => document.removeEventListener('mouseup', handler);
  }, [selectedWord]);

  // ─── Navigation — toujours vider le mot sélectionné ──────────
  const goToList = useCallback(() => {
    setSelectedWord(null);
    setScreen('list');
  }, []);

  const goToGrammar = useCallback(() => {
    setSelectedWord(null);
    setScreen('grammar');
  }, []);

  const goToLyrics = useCallback(() => {
    setScreen('lyrics');
  }, []);

  const handleSelectSong = useCallback((song: BambaraSong) => {
    setSelectedWord(null);
    setCurrentSong(song);
    setScreen('lyrics');
    setLastSongId(song.id);
    try { localStorage.setItem(`${languageCode}-song-last`, song.id); } catch { /* ignore */ }
  }, [languageCode]);

  const closePopup = useCallback(() => setSelectedWord(null), []);

  // ─── Sélection d'un mot ───────────────────────────────────────
  const handleWordTap = useCallback(
    (word: string, lineIdx: number, wordIdx: number) => {
      if (!currentSong) return;
      const annotation = getWordAnnotation(currentSong, word);
      if (!annotation) return;
      // Toggle : re-clic sur le même mot ferme le popup
      if (
        selectedWord &&
        selectedWord.lineIdx === lineIdx &&
        selectedWord.wordIdx === wordIdx
      ) {
        setSelectedWord(null);
        return;
      }
      setSelectedWord({ word, annotation, lineIdx, wordIdx });
      // Mémorise le mot analysé (progression de la chanson)
      const done = readAnalyzed(languageCode, currentSong.id);
      if (!done.includes(word)) {
        try { localStorage.setItem(progressKey(languageCode, currentSong.id), JSON.stringify([...done, word])); } catch { /* ignore */ }
        setProgressTick(n => n + 1);
      }
    },
    [currentSong, selectedWord, languageCode]
  );

  const isHighlighted = (lineIdx: number, wordIdx: number) =>
    selectedWord?.lineIdx === lineIdx && selectedWord?.wordIdx === wordIdx;

  const hasAnnotation = (song: BambaraSong, word: string) =>
    !!getWordAnnotation(song, word);

  // ─── Rendu unique — popup toujours à la racine ────────────────
  return (
    <div className={`sa-screen${screen !== 'grammar' ? ' sm' : ''}`} data-theme={theme}>

      {/* ══ ÉCRAN : BIBLIOTHÈQUE — « Reprendre d'abord » (7b) ═══════ */}
      {screen === 'list' && (
        <>
          <div className="sm-titlebar sm-titlebar--stacked">
            <BackButton onClick={onBack} />
            <div className="sm-titlebar__text">
              <div className="sm-kicker">{t('songAnalyzer.practice', 'PRATIQUER')}</div>
              <h1 className="sm-titlebar__title">{t('songAnalyzer.songs', 'Chansons')}</h1>
            </div>
          </div>

          <div className="sm-body">
            {/* ◆ À L'AFFICHE ◆ (maquette "Chansons", 3a) */}
            {featured && (
              <>
                <div className="sm-kicker">◆ {resumeSong
                  ? t('songAnalyzer.resumeAt', { pct: songProgress(featured), defaultValue: 'REPRENDRE · {{pct}} %' })
                  : t('songAnalyzer.featured', 'À L’AFFICHE')} ◆</div>
                <div className="sm3-feature">
                  <span className="sm3-arch sm3-arch--lg" style={{ background: archColor(featured) }} aria-hidden="true">
                    {featured.title.trim()[0]}
                  </span>
                  <div className="sm3-feature__info">
                    <h2 className="sm3-feature__title">{featured.title}</h2>
                    <span className="sm3-feature__sub">{featured.genre} · {featured.theme}</span>
                    <div className="sm3-chips">
                      <span className="sm3-chip">{t(DIFFICULTY_META[featured.difficulty].labelKey)}</span>
                      <span className="sm3-chip">{t('songAnalyzer.wordsCount', { count: wordCount(featured), defaultValue: '{{count}} mots' })}</span>
                    </div>
                    <span className="sm3-grow" />
                    <button type="button" className="sm3-listen" onClick={() => handleSelectSong(featured)}>
                      <Play size={14} fill="currentColor" strokeWidth={0} />
                      {resumeSong ? t('songAnalyzer.continue', 'Continuer') : t('songAnalyzer.listen', 'Écouter')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ◆ LE RAYON ◆ : les autres chansons, à faire défiler */}
            {otherSongs.length > 0 && (
              <>
                <div className="sm-kicker sm3-gap">◆ {t('songAnalyzer.shelf', 'LE RAYON')} ◆</div>
                <div className="sm3-shelf">
                  {shelfSongs.map(song => {
                    const pct = songProgress(song);
                    return (
                      <button key={song.id} type="button" className="sm3-item" onClick={() => handleSelectSong(song)}>
                        <span className="sm3-arch sm3-arch--sm" style={{ background: archColor(song) }} aria-hidden="true">
                          {song.title.trim()[0]}
                        </span>
                        <span className="sm3-item__title">{song.title}</span>
                        <span className="sm3-item__sub">{song.genre}{pct > 0 ? ` · ${pct} %` : ''}</span>
                      </button>
                    );
                  })}
                  {shelfSongs.length === 0 && <span className="sm-hint">{t('songAnalyzer.noSongInStyle', 'Aucune autre chanson dans ce style.')}</span>}
                </div>

                {/* ◆ PAR STYLE ◆ */}
                {genres.length > 1 && (
                  <>
                    <div className="sm-kicker sm3-gap">◆ {t('songAnalyzer.byStyle', 'PAR STYLE')} ◆</div>
                    <div className="sm3-styles" role="group">
                      {[null, ...genres].map(g => (
                        <button key={g ?? 'all'} type="button" aria-pressed={genreFilter === g}
                          className={`sm3-style${genreFilter === g ? ' is-active' : ''}`} onClick={() => setGenreFilter(g)}>
                          {g ?? t('songAnalyzer.allStyles', 'Tous')}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {languageCode === 'ba' && (
              <button type="button" className="sm-row" onClick={goToGrammar}>
                <span className="sm-row__icon" aria-hidden="true"><BookOpen size={18} /></span>
                <span className="sm-grow">
                  <span className="sm-row__title">{t('songAnalyzer.grammarMapTitle')}</span>
                  <span className="sm-row__sub">{t('songAnalyzer.grammarMapSub')}</span>
                </span>
                <ChevronRight size={16} className="sm-muted" aria-hidden="true" />
              </button>
            )}

            {songs.length === 0 && (
              <div className="sm-empty">{t('songAnalyzer.noSongsYet')}</div>
            )}
          </div>
        </>
      )}

      {/* ══ ÉCRAN : ANALYSER (7d) ═══════════════════════════════════ */}
      {screen === 'lyrics' && currentSong && (
        <>
          <div className="sm-titlebar sm-titlebar--stacked">
            <BackButton onClick={goToList} />
            <div className="sm-titlebar__text">
              <div className="sm-kicker">{currentSong.genre.toUpperCase()} · {t('songAnalyzer.analysis', 'ANALYSE')}</div>
              <h1 className="sm-titlebar__title">{currentSong.title}</h1>
            </div>
          </div>

          <div className="sm-body">
            {languageCode === 'ba' && (
              <div className="sm-tabs" role="tablist">
                <button type="button" role="tab" aria-selected className="sm-tabs__tab is-active">
                  {t('songAnalyzer.analyze', 'Analyser')}
                </button>
                <button type="button" role="tab" aria-selected={false} className="sm-tabs__tab" onClick={goToGrammar}>
                  {t('songAnalyzer.structuresTab', 'Structures')}
                </button>
              </div>
            )}

            <p className="sm-hint">{t('songAnalyzer.tapWordHint')}</p>

            {groupLines(currentSong).map((group, gi) => (
              <React.Fragment key={gi}>
                <div className="sm-kicker sm-kicker--muted">
                  {group.title ? `${group.title} · ` : ''}
                  {group.lines.length > 1
                    ? t('songAnalyzer.linesRange', { from: 1, to: group.lines.length, defaultValue: 'LIGNE {{from}} – {{to}}' })
                    : t('songAnalyzer.lineOne', 'LIGNE 1')}
                </div>

                {group.lines.map(({ line, lineIdx }) => {
                  const pills = (line.structure ?? '').split('+').map(s => s.trim()).filter(Boolean);
                  const keyWords = line.words.filter(w => isKeyWord(currentSong, line, w));
                  const noteWord = !line.grammarNote ? keyWords.find(w => getWordAnnotation(currentSong, w)) : undefined;
                  const audioState = lineAudio[lineIdx];
                  return (
                    <div key={lineIdx} className="sm-line">
                      {pills.length > 0 && (
                        <div className="sm-pills">
                          {pills.map((p, pi) => (
                            <React.Fragment key={pi}>
                              {pi > 0 && <span className="sm-pills__plus">+</span>}
                              <span className="sm-pill">{p.toUpperCase()}</span>
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      <div className="sm-line__row">
                        <div className="sm-line__words">
                          {line.words.map((word, wordIdx) => {
                            const annotable = hasAnnotation(currentSong, word);
                            const active    = isHighlighted(lineIdx, wordIdx);
                            const key       = annotable && keyWords.includes(word);
                            return (
                              <React.Fragment key={wordIdx}>
                                {wordIdx > 0 && ' '}
                                <button
                                  type="button"
                                  className={[
                                    'sm-word',
                                    annotable ? 'is-annotable' : '',
                                    key ? 'is-key' : '',
                                    active ? 'is-active' : '',
                                  ].join(' ')}
                                  onClick={() => annotable && handleWordTap(word, lineIdx, wordIdx)}
                                  disabled={!annotable}
                                  aria-pressed={active}
                                >
                                  {word}
                                </button>
                              </React.Fragment>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className={`sm-play${audioState === 'playing' ? ' is-playing' : ''}${audioState === 'missing' ? ' is-missing' : ''}`}
                          onClick={() => playLine(lineIdx, line.words)}
                          disabled={audioState === 'loading' || audioState === 'missing'}
                          aria-label={audioState === 'missing'
                            ? t('songAnalyzer.audioMissing', 'Audio indisponible') as string
                            : t('songAnalyzer.playLine', 'Écouter la ligne') as string}
                          title={audioState === 'missing' ? t('songAnalyzer.audioMissing', 'Audio indisponible') as string : undefined}
                        >
                          <Play size={13} fill="currentColor" strokeWidth={0} />
                        </button>
                      </div>

                      <div className="sm-line__translation">{getWordTranslation(line, i18n.language)}</div>

                      {(line.grammarNote || noteWord) && (
                        <div className="sm-note">
                          {line.grammarNote
                            ? line.grammarNote
                            : <><strong>{noteWord}</strong> — {getWordAnnotation(currentSong, noteWord!)!.grammarRule}</>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </>
      )}

      {/* ══ ÉCRAN : CARTE GRAMMATICALE ══════════════════════════════ */}
      {screen === 'grammar' && (
        <>
          <div className="sa-grammar-header">
            <BackButton onClick={currentSong ? goToLyrics : goToList} />
            <div className="sa-grammar-title-block">
              <h2 className="sa-grammar-page-title">{t('songAnalyzer.grammarStructures')}</h2>
              <p className="sa-grammar-page-sub">{t('songAnalyzer.grammarPageSub')}</p>
            </div>
          </div>

          <div className="sa-grammar-intro">
            <p>{t('songAnalyzer.grammarIntro')}</p>
          </div>

          <div className="sa-grammar-list">
            {BAMBARA_GRAMMAR_STRUCTURES.map((structure, idx) => {
              const isActive = activeStructure?.id === structure.id;
              return (
                <motion.div
                  key={structure.id}
                  className={['sa-grammar-card', isActive ? 'sa-grammar-card--active' : ''].join(' ')}
                  style={{ '--gs-color': structure.color } as React.CSSProperties}
                  onClick={() => setActiveStructure(isActive ? null : structure)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                >
                  <div className="sa-grammar-card__head">
                    <span className="sa-grammar-card__emoji"><EmojiText text={structure.emoji} /></span>
                    <div className="sa-grammar-card__info">
                      <div className="sa-grammar-card__pattern">{structure.pattern}</div>
                      <div className="sa-grammar-card__label">{structure.label}</div>
                    </div>
                    <span className="sa-grammar-card__toggle">{isActive ? '▲' : '▼'}</span>
                  </div>

                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        className="sa-grammar-card__body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                      >
                        <p className="sa-grammar-card__desc">{structure.description}</p>
                        <div className="sa-grammar-card__examples">
                          {structure.examples.map((ex, i) => {
                            const parts = ex.bambara.split(' ');
                            return (
                              <div key={i} className="sa-grammar-example">
                                <div className="sa-grammar-example__bambara">
                                  {parts.map((part, pi) => (
                                    <span
                                      key={pi}
                                      className={[
                                        'sa-grammar-example__token',
                                        ex.highlight.includes(pi) ? 'sa-grammar-example__token--hl' : '',
                                      ].join(' ')}
                                      style={ex.highlight.includes(pi) ? { color: structure.color } : {}}
                                    >
                                      {part}
                                    </span>
                                  ))}
                                </div>
                                <div className="sa-grammar-example__fr">{ex.french}</div>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          <div className="sa-grammar-footer">
            <div className="sa-sov-diagram">
              <div className="sa-sov-box sa-sov-box--s">S<span>{t('songAnalyzer.subject')}</span></div>
              <div className="sa-sov-sep">+</div>
              <div className="sa-sov-box sa-sov-box--m">{t('songAnalyzer.marker')}<span>YE / BƐ / TƐ / KA</span></div>
              <div className="sa-sov-sep">+</div>
              <div className="sa-sov-box sa-sov-box--v">V<span>{t('songAnalyzer.verb')}</span></div>
            </div>
            <p className="sa-grammar-footer__note">
              {t('songAnalyzer.postpositionsNoteBefore')}<strong>{t('songAnalyzer.postpositionsNoteStrong')}</strong>{t('songAnalyzer.postpositionsNoteAfter')}
            </p>
          </div>
        </>
      )}

      {/* ══ POPUP MOT — À LA RACINE, hors des blocs conditionnels ══
          AnimatePresence peut toujours animer l'exit car son parent
          n'est jamais démonté lors des changements d'écran.           */}
      <AnimatePresence>
        {selectedWord && (
          <motion.div
            className="sa-popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, pointerEvents: 'none' }}
            transition={{ duration: 0.15 }}
          >
            <motion.div
              ref={popupRef}
              className="sa-popup"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            >
              <button
                className="sa-popup__close"
                onClick={closePopup}
                aria-label={t('revisionStats.close') as string}
              >
                <X size={16} />
              </button>

              <div className="sa-popup__word">{selectedWord.word}</div>
              <div className="sa-popup__translation">
                {getWordTranslation(selectedWord.annotation, i18n.language)}
              </div>

              {(() => {
                const cat   = selectedWord.annotation.grammaticalCategory;
                const style = CATEGORY_COLORS[cat] ?? { bg: '#f5f5f5', color: '#333' };
                return (
                  <span
                    className="sa-popup__cat-badge"
                    style={{ background: style.bg, color: style.color }}
                  >
                    {CATEGORY_LABEL_KEYS[cat] ? t(CATEGORY_LABEL_KEYS[cat]) : cat}
                  </span>
                );
              })()}

              <div className="sa-popup__section-title">{t('songAnalyzer.grammarRule')}</div>
              <p className="sa-popup__rule">{selectedWord.annotation.grammarRule}</p>

              <div className="sa-popup__section-title">{t('songAnalyzer.example')}</div>
              <div className="sa-popup__example">
                <span className="sa-popup__example-bambara">
                  {selectedWord.annotation.example}
                </span>
                <span className="sa-popup__example-fr">
                  {i18n.language === 'en' && selectedWord.annotation.exampleTranslationEn
                    ? selectedWord.annotation.exampleTranslationEn
                    : selectedWord.annotation.exampleTranslation}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SongAnalyzer;
